// frontend/src/theme.js

import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';
import '@fontsource/roboto';

export const BRAND = {
    primary: '#e72d88',
    primaryLight: '#f7b6d6',
    primaryDark: '#a9165f',
    dark: '#1f2a44',
    darkMuted: '#33415f',
    secondary: '#1268b3',
    secondaryLight: '#4a9bd3',
    secondaryDark: '#0b4f8a',
    surface: '#f5f7fb',
    surfaceAlt: '#edf2f8',
    paper: '#ffffff',
};

const buttonContainedStyles = {
    backgroundColor: BRAND.secondary,
    color: '#ffffff',
    boxShadow: `0 2px 6px ${alpha(BRAND.secondary, 0.22)}`,
    '&:hover': {
        backgroundColor: BRAND.secondaryDark,
        color: '#ffffff',
        boxShadow: `0 4px 10px ${alpha(BRAND.secondaryDark, 0.24)}`,
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            color: '#ffffff',
        },
    },
    '&:active': {
        backgroundColor: BRAND.secondaryDark,
        boxShadow: `0 1px 3px ${alpha(BRAND.secondaryDark, 0.2)}`,
    },
    '&.Mui-disabled': {
        backgroundColor: alpha(BRAND.dark, 0.14),
        color: alpha('#ffffff', 0.72),
        boxShadow: 'none',
    },
};

let theme = createTheme({
    typography: {
        fontFamily: 'Roboto, sans-serif',
        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            fontStyle: 'normal',
            '@media (max-width:600px)': {
                fontSize: '2rem',
            },
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 700,
            fontStyle: 'normal',
            '@media (max-width:600px)': {
                fontSize: '1.75rem',
            },
        },
        h3: {
            fontSize: '1.75rem',
            fontWeight: 600,
            fontStyle: 'normal',
            '@media (max-width:600px)': {
                fontSize: '1.5rem',
            },
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 600,
            fontStyle: 'normal',
            '@media (max-width:600px)': {
                fontSize: '1.25rem',
            },
        },
        h5: {
            fontSize: '1.25rem',
            fontWeight: 600,
            fontStyle: 'normal',
            '@media (max-width:600px)': {
                fontSize: '1rem',
            },
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            fontStyle: 'normal',
            '@media (max-width:600px)': {
                fontSize: '0.875rem',
            },
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 500,
            fontStyle: 'normal',
            '@media (max-width:600px)': {
                fontSize: '0.875rem',
            },
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 500,
            fontStyle: 'normal',
            '@media (max-width:600px)': {
                fontSize: '0.75rem',
            },
        },
        p: {
            variant: 'body1',
        },
        allVariants: {
            color: BRAND.dark,
        },
        button: {
            fontWeight: 700,
            textTransform: 'none',
        },
    },
    palette: {
        primary: {
            main: BRAND.primary,
            light: BRAND.primaryLight,
            dark: BRAND.primaryDark,
            contrastText: '#ffffff',
        },
        secondary: {
            main: BRAND.secondary,
            light: BRAND.secondaryLight,
            dark: BRAND.secondaryDark,
            contrastText: '#ffffff',
        },
        dark: {
            main: BRAND.dark,
            light: BRAND.darkMuted,
            dark: '#121010',
            contrastText: '#ffffff',
        },
        text: {
            primary: BRAND.dark,
            secondary: alpha(BRAND.dark, 0.68),
            disabled: alpha(BRAND.dark, 0.42),
        },
        divider: alpha(BRAND.dark, 0.1),
        background: {
            default: BRAND.surface,
            paper: BRAND.paper,
        },
        action: {
            hover: alpha(BRAND.secondary, 0.055),
            selected: alpha(BRAND.primary, 0.08),
            focus: alpha(BRAND.secondary, 0.16),
        },
        dispatch: {
            green: '#20c997',
            orange: '#ff9800',
            red: '#dc3545',
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                ':root': {
                    '--brand-primary': BRAND.primary,
                    '--brand-dark': BRAND.dark,
                    '--brand-secondary': BRAND.secondary,
                    '--brand-surface': BRAND.surface,
                },
                body: {
                    backgroundColor: BRAND.surface,
                    color: BRAND.dark,
                },
                'a:not(.MuiButton-root)': {
                    color: BRAND.secondary,
                },
                'a.MuiButton-root': {
                    textDecoration: 'none',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    fontWeight: 800,
                    textTransform: 'none',
                    minHeight: 38,
                    padding: '8px 16px',
                    lineHeight: 1.18,
                    letterSpacing: 0,
                    transition: 'background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, color 140ms ease',
                    '& .MuiButton-startIcon': {
                        marginRight: 7,
                    },
                    '& .MuiButton-endIcon': {
                        marginLeft: 7,
                    },
                    '&:focus-visible': {
                        outline: `3px solid ${alpha(BRAND.secondary, 0.22)}`,
                        outlineOffset: 2,
                    },
                },
                sizeSmall: {
                    minHeight: 32,
                    padding: '6px 12px',
                    fontSize: '0.8125rem',
                },
                sizeLarge: {
                    minHeight: 44,
                    padding: '10px 20px',
                },
                containedPrimary: buttonContainedStyles,
                containedSecondary: buttonContainedStyles,
                containedInherit: {
                    backgroundColor: BRAND.dark,
                    color: '#ffffff',
                    boxShadow: `0 2px 6px ${alpha(BRAND.dark, 0.18)}`,
                    '&:hover': {
                        backgroundColor: BRAND.darkMuted,
                        color: '#ffffff',
                        boxShadow: `0 4px 10px ${alpha(BRAND.dark, 0.2)}`,
                    },
                },
                outlined: {
                    borderColor: alpha(BRAND.dark, 0.22),
                    color: BRAND.dark,
                    backgroundColor: '#ffffff',
                    '&:hover': {
                        borderColor: BRAND.secondary,
                        backgroundColor: alpha(BRAND.secondary, 0.07),
                        color: BRAND.secondaryDark,
                    },
                },
                outlinedPrimary: {
                    borderColor: alpha(BRAND.secondary, 0.34),
                    color: BRAND.secondaryDark,
                    '&:hover': {
                        borderColor: BRAND.secondary,
                        backgroundColor: alpha(BRAND.secondary, 0.07),
                        color: BRAND.secondaryDark,
                    },
                },
                outlinedSecondary: {
                    borderColor: alpha(BRAND.secondary, 0.35),
                    color: BRAND.secondary,
                    '&:hover': {
                        borderColor: BRAND.secondary,
                        backgroundColor: alpha(BRAND.secondary, 0.07),
                        color: BRAND.secondaryDark,
                    },
                },
                text: {
                    color: BRAND.dark,
                    '&:hover': {
                        backgroundColor: alpha(BRAND.primary, 0.065),
                        color: BRAND.primaryDark,
                    },
                },
                textPrimary: {
                    color: BRAND.dark,
                    '&:hover': {
                        backgroundColor: alpha(BRAND.primary, 0.065),
                        color: BRAND.primaryDark,
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: BRAND.dark,
                    color: '#ffffff',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: BRAND.secondary,
                    fontWeight: 600,
                    '&:hover': {
                        color: BRAND.primaryDark,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                colorPrimary: {
                    backgroundColor: alpha(BRAND.primary, 0.1),
                    color: BRAND.primaryDark,
                    fontWeight: 700,
                },
                colorSecondary: {
                    backgroundColor: alpha(BRAND.secondary, 0.12),
                    color: BRAND.secondaryDark,
                    fontWeight: 700,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: BRAND.primary,
                    height: 3,
                    borderRadius: 99,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 800,
                    '&.Mui-selected': {
                        color: BRAND.primaryDark,
                    },
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 800,
                    color: alpha(BRAND.dark, 0.72),
                    borderColor: alpha(BRAND.dark, 0.14),
                    '&.Mui-selected': {
                        backgroundColor: BRAND.secondary,
                        color: '#ffffff',
                        borderColor: BRAND.secondary,
                        '&:hover': {
                            backgroundColor: BRAND.secondaryDark,
                        },
                    },
                    '&:hover': {
                        backgroundColor: alpha(BRAND.secondary, 0.06),
                    },
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff',
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    borderCollapse: 'separate',
                    borderSpacing: '0 8px',
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(BRAND.dark, 0.03),
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: alpha(BRAND.secondary, 0.035),
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '12px 16px',
                    borderBottom: 'none',
                },
                head: {
                    fontWeight: 800,
                    color: BRAND.dark,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    fontSize: '0.72rem',
                },
                body: {
                    color: alpha(BRAND.dark, 0.82),
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    borderTop: `1px solid ${alpha(BRAND.dark, 0.08)}`,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                outlined: {
                    borderColor: alpha(BRAND.dark, 0.1),
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: BRAND.secondary,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: BRAND.secondary,
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: alpha(BRAND.dark, 0.42),
                    '&.Mui-checked': {
                        color: BRAND.primary,
                    },
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(BRAND.primary, 0.12),
                },
                bar: {
                    backgroundColor: BRAND.primary,
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    color: BRAND.primary,
                },
            },
        },
    },
});

theme = responsiveFontSizes(theme, {
    breakpoints: ['xs', 'sm', 'md', 'lg', 'xl'],
    factor: 2,
});

export default theme;
